import { BotId } from '@feedyou/schema'
import { FeedbotDispatch } from '../../../store/types'
import { fixBrokenComment, fixBrokenComments } from './utils/fixBrokenComments'

import { serverApi, ServerApiTag } from '../../../store/serverApi'
import { setTimestampToNow } from './utils/setTimestampToNow'

import {
  denormalizeComment,
  DenormalizedComment,
  denormalizePatch,
  normalizeComment,
  normalizeComments
} from './utils/dtoParsers'
import {
  GetCommentsArgs,
  NormalizedCommentsWithDeps,
  SubmitNewCommentArgs,
  UpdateCommentArgs
} from './types'
import { emitCommentChange } from '../../../store/socketServer/socketServerConnectionService'

export const commentsApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getComments: builder.query<NormalizedCommentsWithDeps, GetCommentsArgs>({
      query: args => `bots/${args.botId}/comments`,
      serializeQueryArgs: ({ queryArgs }) => `bots/${queryArgs.botId}/comments`,
      transformResponse: (comments: DenormalizedComment[]) => {
        return fixBrokenComments(normalizeComments(comments))
      },
      providesTags: [ServerApiTag.COMMENTS]
    }),

    updateComment: builder.mutation<
      { comments: DenormalizedComment | DenormalizedComment[] },
      UpdateCommentArgs
    >({
      query: ({ botId, patch, authors }) => ({
        url: `bots/${botId}/comments/`,
        method: 'PATCH',
        body: denormalizePatch(patch, authors)
      }),
      onQueryStarted: async ({ botId }, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          mergeCommentIntoCache(result.data.comments, botId, dispatch, commentsApi)
          emitCommentChange()
        } catch (e) {}
      }
    }),

    submitNewComment: builder.mutation<{ comment: DenormalizedComment }, SubmitNewCommentArgs>({
      query: ({ botId, comment, authors }) => ({
        url: `bots/${botId}/comments/`,
        method: 'POST',
        body: setTimestampToNow(denormalizeComment(comment, authors))
      }),
      onQueryStarted: async ({ botId }, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          mergeCommentIntoCache(result.data.comment, botId, dispatch, commentsApi)
          emitCommentChange()
        } catch (e) {}
      }
    })
  })
})

export const mergeCommentIntoCache = (
  comment: DenormalizedComment | DenormalizedComment[],
  botId: BotId,
  dispatch: FeedbotDispatch,
  api: typeof commentsApi
) => {
  return dispatch(
    api.util.updateQueryData('getComments', { botId }, draft => {
      const comments = Array.isArray(comment) ? comment : [comment]
      comments
        .filter(comment => comment.id !== 'status')
        .forEach(comment => {
          draft.commentsById[comment.id] = fixBrokenComment(normalizeComment(comment))

          const author = comment.author
          const resolver = comment.resolved?.by

          if (author?.id) {
            draft.authorsById[author.id] = author
          }

          if (resolver?.id) {
            draft.authorsById[resolver.id] = resolver
          }
        })
    })
  )
}

export const invalidateCommentsCache = () =>
  commentsApi.util.invalidateTags([ServerApiTag.COMMENTS])
